import { zodResolver } from '@hookform/resolvers/zod';
import { FormField } from '@pm/forms';
import { useRequestMagicLinkMutation } from '@pm/graphql';
import {
  Banner,
  Button,
  ButtonLink,
  InlineMessage,
  Input,
  Text,
  useToast,
} from '@pm/ui';
import * as Sentry from '@sentry/react';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router';
import { z } from 'zod';
import { detectUserErrors } from '../../modules/errors/errors';
import MailIcon from './mail.svg?react';

export const MagicLinkForm = () => {
  const { t } = useTranslation('global', { keyPrefix: 'MagicLink' });
  const { t: tErrors } = useTranslation('global', {
    keyPrefix: 'Errors.Generic',
  });
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [requestMagicLink, { loading }] = useRequestMagicLinkMutation();
  const successRef = useRef<HTMLDivElement>(null);
  const { negative } = useToast();
  const { search } = useLocation();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email') ?? '';
  const redirectUrlParam = searchParams.get('redirect_url') ?? undefined;
  const isExpired = searchParams.get('reason') === 'expired' && !sentTo;

  useEffect(() => {
    if (sentTo) successRef.current?.focus();
  }, [sentTo]);

  const schema = z.object({
    email: z.string().min(1, t('MissingEmail')).email(t('InvalidEmail')),
  });

  type MagicLinkFormType = z.infer<typeof schema>;

  const methods = useForm<MagicLinkFormType>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { email: emailParam },
  });

  useEffect(() => {
    if (emailParam) methods.trigger('email');
  }, [emailParam, methods]);

  const onSubmit = async ({ email }: MagicLinkFormType) => {
    try {
      const { data } = await requestMagicLink({
        variables: { input: { email, redirectUrl: redirectUrlParam } },
      });
      detectUserErrors(data, (res) => res.requestMagicLink?.errors);
      setSentTo(email);
    } catch (err) {
      negative({
        title: tErrors('Title'),
        content: tErrors('Instructions'),
      });
      Sentry.captureException(
        `[MagicLinkForm] Error requesting magic link: ${err}`,
      );
    }
  };

  const submitDisabled = !methods.formState.isValid || loading;

  if (sentTo) {
    return (
      <div
        ref={successRef}
        role="status"
        aria-live="polite"
        tabIndex={-1}
        className="flex flex-col items-center space-y-l text-center outline-hidden"
      >
        <MailIcon
          aria-hidden="true"
          className="h-24 w-24"
          style={{ fill: 'rgb(var(--content-link))' }}
        />
        <Text size="headingM">{t('CheckInboxTitle')}</Text>
        <Text color="contentSubdued">
          <Trans
            t={t}
            i18nKey="CheckInboxBody"
            values={{ email: sentTo }}
            components={{ strong: <strong /> }}
          />
        </Text>
        <ButtonLink
          to={{ pathname: '/', search }}
          intent="secondary"
          width="full"
        >
          {t('BackToLogin')}
        </ButtonLink>
      </div>
    );
  }

  return (
    <div className="space-y-xxxl">
      <Text size="headingM">{t('Title')}</Text>
      <main>
        <p className="text-content-subdued">{t('Description')}</p>
        {isExpired && (
          <div className="mt-l">
            <Banner intent="warning" content={t('LinkExpired')} />
          </div>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="space-y-l mt-l">
              <FormField
                id="email"
                type="email"
                label={t('Email')}
                name="email"
                placeholder={t('EmailPlaceholder')}
                space="xxs"
                Component={Input}
              />
            </div>
            <div className="mt-l">
              <InlineMessage
                intent="info"
                size="small"
                instructions={t('Disclaimer')}
              />
            </div>
            <div className="mt-xxxl space-y-xxs">
              <Button type="submit" width="full" disabled={submitDisabled}>
                {t('Submit')}
              </Button>
              <ButtonLink
                to={{ pathname: '/', search }}
                intent="secondary"
                width="full"
              >
                {t('BackToLogin')}
              </ButtonLink>
            </div>
          </form>
        </FormProvider>
      </main>
    </div>
  );
};
